import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {Split, Stack} from '@dropbox/dig-foundations';
import {TeamLine} from '@dropbox/dig-icons/assets';
import {openAiSearchAndAskAtom} from 'atoms/aiSearch';
import {Employee} from 'client';
import {Avatar, AvatarIcon} from 'components/DSYS/Avatar';
import type {ChatMention} from 'components/DSYS/chat/chatMentions';
import {Link} from 'components/DSYS/Link';
import {Title} from 'components/DSYS/Title';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {Trans} from 'react-i18next';
import {formatAgendaLocation} from 'views/home/dashboard/tiles/agendaDescriptionLinks';

import {InviteLinks, MeetingButtons} from './MeetingLinks';
import {SearchRow} from './searchRows';
import {peopleCount} from './TeamCards';
import {AskRow, Label, Meta, Row, RowList} from './Typography';
import {formatMeetingTime, Meeting, MeetingAttendee} from './useMeetings';

export const meetingRow = (meeting: Meeting): SearchRow => ({
  kind: 'meeting',
  id: `meeting:${meeting.id}`,
  meeting,
});

/** In place of Coming up when no calendar is linked: the agenda tile's own hint. */
export const LinkCalendarHint = () => (
  <Text size="small" color="subtle" tagName="div">
    <Trans
      i18nKey="agenda_empty_hint"
      t={t}
      components={{
        Link: <Link monochromatic to="https://www.dropbox.com/apps/google_deep_integration" />,
      }}
    />
  </Text>
);

export const mentionsFor = (meeting: Meeting): ChatMention[] =>
  meeting.attendees.flatMap(({person}) =>
    person ? [{employee_id: person.user_id, name: person.name, ldap: person.ldap}] : []
  );

/** What to ask about a meeting: the label shown, and the question sent. */
const meetingAsks = ({title}: Meeting) => [
  {
    label: t('search_box_meeting_prep_label'),
    question: t('search_box_meeting_prep_question', {title}),
  },
  {
    label: t('search_box_meeting_people_label'),
    question: t('search_box_meeting_people_question', {title}),
  },
];

/** An unmatched Dropbox address is a mailing list or group, not a guest. */
const isGroup = (attendee: MeetingAttendee) =>
  !attendee.person && Boolean(attendee.email?.toLowerCase().endsWith('@dropbox.com'));

/**
 * A meeting in the panel: the title (the view's one bold line), what to ask
 * about it (the attendees go along as mentions), then who's in it; click a person
 * to open them here.
 */
export const MeetingDetail = ({
  meeting,
  onSelect,
  hideTitle = false,
}: {
  meeting: Meeting;
  onSelect: (person: Employee) => void;
  /** Inside an open list row that already shows the title and time. */
  hideTitle?: boolean;
}) => {
  const openAiSearchAndAsk = useSetAtom(openAiSearchAndAskAtom);
  const ask = (question: string) =>
    openAiSearchAndAsk(question, 'search_suggestion', {mentions: mentionsFor(meeting)});

  return (
    <Stack gap="44">
      <Stack gap="16">
        {hideTitle ? (
          <Split gap="8" alignY="center">
            <MeetingButtons meeting={meeting} />
          </Split>
        ) : (
          <Stack gap="4">
            <Title size={18}>{meeting.title}</Title>
            <Split gap="8" alignY="center">
              <Split.Item style={{minWidth: 0}}>
                <Text color="subtle" tagName="div">
                  {[
                    formatMeetingTime(meeting.start),
                    formatAgendaLocation(meeting.location ?? undefined),
                    peopleCount(meeting.attendeeCount),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </Split.Item>
              <MeetingButtons meeting={meeting} />
            </Split>
          </Stack>
        )}
        <InviteLinks meeting={meeting} />
      </Stack>

      {/* What to ask comes first, unlabeled, like everywhere in the panel. */}
      <Stack gap="8">
        <RowList>
          {meetingAsks(meeting).map(({label, question}) => (
            <AskRow key={label} label={label} onClick={() => ask(question)} />
          ))}
        </RowList>
      </Stack>

      <Stack gap="12">
        <Label>{t('people')}</Label>
        <RowList>
          {meeting.attendees.map((attendee) => (
            <Row
              key={attendee.key}
              onClick={attendee.person ? () => onSelect(attendee.person!) : undefined}
            >
              <Split gap="12" alignY="center">
                <Split.Item>
                  {isGroup(attendee) ? (
                    <AvatarIcon src={TeamLine} size="medium" />
                  ) : (
                    <Avatar user={attendee.person ?? {name: attendee.name}} size="medium" />
                  )}
                </Split.Item>
                <Split.Item width="fill" style={{minWidth: 0}}>
                  <Text tagName="div">
                    <Truncate>{attendee.name}</Truncate>
                  </Text>
                  <Meta>
                    {[
                      attendee.person?.role ??
                        t(
                          isGroup(attendee)
                            ? 'search_box_attendee_group'
                            : 'search_box_attendee_outside'
                        ),
                      attendee.isOrganizer ? t('search_box_attendee_organizer') : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Meta>
                </Split.Item>
              </Split>
            </Row>
          ))}
        </RowList>
      </Stack>
    </Stack>
  );
};
